import React, { useState, useEffect } from "react";

const IRRCalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState(500000);
  const [year1CashFlow, setYear1CashFlow] = useState(150000);
  const [year2CashFlow, setYear2CashFlow] = useState(180000);
  const [year3CashFlow, setYear3CashFlow] = useState(200000);
  const [year4CashFlow, setYear4CashFlow] = useState(220000);
  const [year5CashFlow, setYear5CashFlow] = useState(250000);
  const [requiredReturn, setRequiredReturn] = useState(12);
  const [periodType, setPeriodType] = useState("annual");
  const [result, setResult] = useState({
    irr: "0.00",
    npvAtIRR: "0.00",
    totalCashInflows: "0.00",
    totalProfit: "0.00",
    roi: "0.00",
    paybackPeriod: "0.00",
    investmentDecision: "",
    investmentRating: "",
  });

  // IRR Calculation Function using Newton-Raphson Method
  const calculateIRR = () => {
    const initial = parseFloat(initialInvestment);
    const cf1 = parseFloat(year1CashFlow);
    const cf2 = parseFloat(year2CashFlow);
    const cf3 = parseFloat(year3CashFlow);
    const cf4 = parseFloat(year4CashFlow);
    const cf5 = parseFloat(year5CashFlow);
    const reqReturn = parseFloat(requiredReturn);

    if (isNaN(initial) || initial <= 0) return;

    const cashFlows = [cf1, cf2, cf3, cf4, cf5];
    const totalCashInflows = cashFlows.reduce((sum, cf) => sum + cf, 0);
    const totalProfit = totalCashInflows - initial;
    const roi = (totalProfit / initial) * 100;

    // Calculate IRR using Newton-Raphson method
    let irr = 0.1; // Initial guess 10%
    let iterations = 0;
    const maxIterations = 1000;
    const tolerance = 0.00001;

    while (iterations < maxIterations) {
      let npv = -initial;
      let derivative = 0;
      
      for (let i = 0; i < cashFlows.length; i++) {
        const period = i + 1;
        npv += cashFlows[i] / Math.pow(1 + irr, period);
        derivative -= (period * cashFlows[i]) / Math.pow(1 + irr, period + 1);
      }
      
      if (Math.abs(npv) < tolerance) break;
      if (derivative === 0) break;
      
      const newIRR = irr - npv / derivative;
      
      // Prevent wild jumps
      if (Math.abs(newIRR - irr) > 1) {
        irr = irr - (npv > 0 ? 0.1 : -0.1);
      } else {
        irr = newIRR;
      }
      
      iterations++;
    }

    // Check if IRR calculation was successful
    const irrResult = (iterations < maxIterations && irr > -0.99 && irr < 10) ? irr * 100 : null;

    // Calculate NPV at IRR (should be ~0)
    let npvAtIRR = -initial;
    if (irrResult !== null) {
      for (let i = 0; i < cashFlows.length; i++) {
        npvAtIRR += cashFlows[i] / Math.pow(1 + irr, i + 1);
      }
    }

    // Calculate Payback Period
    let cumulativeCashFlow = 0;
    let paybackPeriod = 0;
    
    for (let i = 0; i < cashFlows.length; i++) {
      cumulativeCashFlow += cashFlows[i];
      if (cumulativeCashFlow >= initial) {
        const remainingAmount = initial - (cumulativeCashFlow - cashFlows[i]);
        paybackPeriod = i + 1 + (remainingAmount / cashFlows[i]) - 1;
        break;
      }
    }
    
    if (cumulativeCashFlow < initial) {
      paybackPeriod = null;
    }

    let investmentDecision = "";
    let investmentRating = "";

    if (irrResult !== null) {
      if (irrResult > reqReturn) {
        investmentDecision = "Accept";
        const spread = irrResult - reqReturn;
        if (spread > 10) {
          investmentRating = "Excellent Investment";
        } else if (spread > 5) {
          investmentRating = "Very Good Investment";
        } else if (spread > 2) {
          investmentRating = "Good Investment";
        } else {
          investmentRating = "Marginal Investment";
        }
      } else if (Math.abs(irrResult - reqReturn) < 0.5) {
        investmentDecision = "Neutral";
        investmentRating = "Break-even Investment";
      } else {
        investmentDecision = "Reject";
        investmentRating = "Below Required Return";
      }
    } else {
      investmentDecision = "Cannot Calculate";
      investmentRating = "Invalid Cash Flows";
    }

    setResult({
      irr: irrResult !== null ? irrResult.toFixed(2) : "N/A",
      npvAtIRR: npvAtIRR.toFixed(2),
      totalCashInflows: totalCashInflows.toFixed(2),
      totalProfit: totalProfit.toFixed(2),
      roi: roi.toFixed(2),
      paybackPeriod: paybackPeriod !== null ? paybackPeriod.toFixed(2) : "Beyond 5 years",
      investmentDecision,
      investmentRating,
    });
  };

  useEffect(() => {
    calculateIRR();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <h1>IRR Calculator (Internal Rate of Return)</h1>
          <p>
            Calculate the Internal Rate of Return to evaluate investment profitability and compare alternatives
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Investment & Cash Flow Details
              </h2>

              {/* Initial Investment */}
              <div className="input-group">
                <label>Initial Investment (Outflow)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={initialInvestment}
                    onChange={(e) => setInitialInvestment(e.target.value)}
                    className="calc-input"
                    placeholder="500000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setInitialInvestment(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Required Return */}
              <div className="input-group">
                <label>Required/Hurdle Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={requiredReturn}
                    onChange={(e) => setRequiredReturn(e.target.value)}
                    className="calc-input"
                    placeholder="12"
                    step="0.5"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[10, 12, 15, 18].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setRequiredReturn(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Period Type */}
              <div className="input-group">
                <label>Cash Flow Period</label>
                <div className="quick-select-group">
                  {[
                    { value: "annual", label: "Annual" },
                    { value: "quarterly", label: "Quarterly" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setPeriodType(type.value)}
                      className={`quick-select-btn ${periodType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Year 1 Cash Flow */}
              <div className="input-group">
                <label>Year 1 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year1CashFlow}
                    onChange={(e) => setYear1CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="150000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 150000, 200000, 300000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setYear1CashFlow(amount)}
                      className="quick-select-btn success"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Year 2 Cash Flow */}
              <div className="input-group">
                <label>Year 2 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year2CashFlow}
                    onChange={(e) => setYear2CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="180000"
                  />
                </div>
              </div>

              {/* Year 3 Cash Flow */}
              <div className="input-group">
                <label>Year 3 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year3CashFlow}
                    onChange={(e) => setYear3CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="200000"
                  />
                </div>
              </div>

              {/* Year 4 Cash Flow */}
              <div className="input-group">
                <label>Year 4 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year4CashFlow}
                    onChange={(e) => setYear4CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="220000"
                  />
                </div>
              </div>

              {/* Year 5 Cash Flow */}
              <div className="input-group">
                <label>Year 5 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year5CashFlow}
                    onChange={(e) => setYear5CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="250000"
                  />
                </div>
              </div>

              <button onClick={calculateIRR} className="calculate-btn">
                Calculate IRR
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Return Analysis
              </h2>

              <div className="result-card">
                {/* IRR - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Internal Rate of Return</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.irr === "N/A" ? "N/A" : `${result.irr}%`}
                  </div>
                  <div className="result-subtext">
                    {result.investmentDecision} - {result.investmentRating}
                  </div>
                </div>

                {/* Total Return */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Cash Inflows</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalCashInflows).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Over investment period
                  </div>
                </div>

                {/* Simple ROI */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Simple ROI</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                        <polyline points="17 6 23 6 23 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.roi}%
                  </div>
                  <div className="result-subtext">
                    Total profit percentage
                  </div>
                </div>

                {/* Payback Period */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Payback Period</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.paybackPeriod === "Beyond 5 years" ? result.paybackPeriod : `${result.paybackPeriod} years`}
                  </div>
                  <div className="result-subtext">
                    Time to recover investment
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Investment Comparison</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">IRR</span>
                      <span className="breakdown-percentage">
                        {result.irr === "N/A" ? "N/A" : `${result.irr}%`}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: result.irr === "N/A" ? "0%" : `${Math.min((parseFloat(result.irr) / 30) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Required Return</span>
                      <span className="breakdown-percentage">
                        {requiredReturn}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${Math.min((requiredReturn / 30) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total Profit</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.totalProfit).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className={`breakdown-bar-fill ${parseFloat(result.totalProfit) >= 0 ? 'green' : 'blue'}`}
                        style={{
                          width: `${Math.min((Math.abs(parseFloat(result.totalProfit)) / initialInvestment) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Decision Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: result.irr !== "N/A" && parseFloat(result.irr) > requiredReturn ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', border: `1px solid ${result.irr !== "N/A" && parseFloat(result.irr) > requiredReturn ? '#10b981' : '#ef4444'}`}}>
                  <h3 className="breakdown-title" style={{color: result.irr !== "N/A" && parseFloat(result.irr) > requiredReturn ? '#065f46' : '#991b1b', marginBottom: '0.5rem'}}>Investment Decision</h3>
                  <p style={{fontSize: '0.875rem', color: result.irr !== "N/A" && parseFloat(result.irr) > requiredReturn ? '#065f46' : '#991b1b', lineHeight: '1.5', margin: 0}}>
                    {result.irr !== "N/A" && parseFloat(result.irr) > requiredReturn
                      ? `The IRR of ${result.irr}% exceeds your required return of ${requiredReturn}%, making this a profitable investment. Accept this project as it generates returns above your hurdle rate.`
                      : result.irr !== "N/A" && parseFloat(result.irr) < requiredReturn
                        ? `The IRR of ${result.irr}% is below your required return of ${requiredReturn}%. This investment doesn't meet your minimum return threshold and should be rejected.`
                        : result.irr !== "N/A"
                          ? `The IRR of ${result.irr}% equals your required return. This is a break-even investment with no value creation.`
                          : "Cannot calculate IRR with the provided cash flows. Check that you have positive cash inflows that exceed the initial investment."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Internal Rate of Return (IRR)</h3>
              <p>
                Internal Rate of Return (IRR) is the discount rate at which the Net Present Value (NPV) of an investment 
                equals zero. In simpler terms, it's the annualized effective compound return rate that makes the present 
                value of all future cash flows equal to the initial investment. IRR represents the "break-even" return 
                rate of a project. If IRR exceeds your required rate of return (hurdle rate or cost of capital), the 
                investment is profitable - accept it. If IRR is below your hurdle rate, the investment destroys value - 
                reject it. IRR is intuitive because it expresses returns as a percentage, similar to interest rates, 
                making it easy to compare with other investments or benchmarks. It's widely used for capital budgeting, 
                real estate investments, private equity, and project evaluation. However, IRR has limitations: it assumes 
                reinvestment of cash flows at the IRR rate (often unrealistic), can produce multiple values with 
                unconventional cash flows, and may rank projects incorrectly when comparing mutually exclusive investments 
                of different scales.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>IRR Concept:</h4>
            <div className="formula-display">
              0 = Σ [CFt ÷ (1 + IRR)^t] - Initial Investment
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">IRR</div>
                <div className="formula-param-value">The rate where NPV = 0</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Decision Rule</div>
                <div className="formula-param-value">Accept if IRR > Required Return</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">CFt</div>
                <div className="formula-param-value">Cash flow at time t</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Hurdle Rate</div>
                <div className="formula-param-value">Minimum acceptable return (WACC/Cost of Capital)</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How is IRR different from ROI?</h5>
              <p>
                IRR shows percentage return - answering "what rate of return do I earn?" which managers often find 
                more intuitive and easier to communicate. Use NPV as your primary decision criterion: accept all projects 
                with positive NPV when capital is unlimited. Use IRR as a supplementary metric to communicate return 
                expectations and screen projects quickly. When NPV and IRR conflict (rare but possible), trust NPV. For 
                comparing mutually exclusive projects, use NPV to maximize value. 
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate IRR manually without a calculator?</h5>
              <p>
                Calculating IRR manually is complex because it requires solving for the rate in a polynomial equation, 
                which has no direct algebraic solution. However, you can approximate IRR using trial and error or 
                interpolation. Start by guessing a discount rate (say 10%) and calculating NPV. If NPV is positive, 
                the true IRR is higher - try 15%. If NPV is negative, IRR is lower - try 5%. Continue narrowing the 
                range until NPV is close to zero. For better accuracy, use linear interpolation: if 10% gives NPV of 
                ₹50,000 and 15% gives NPV of -₹30,000, IRR is approximately 10% + [(50,000)/(50,000+30,000)] × 5% = 
                13.125%.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is Modified IRR (MIRR) and when should I use it?</h5>
              <p>
                Modified Internal Rate of Return (MIRR) addresses IRR's unrealistic reinvestment assumption. While IRR 
                assumes cash flows are reinvested at the IRR rate itself, MIRR uses two separate rates: financing rate 
                (cost of capital for negative cash flows) and reinvestment rate (realistic rate for positive cash flows, 
                typically WACC or market rate). MIRR calculation: compound all positive cash flows forward to project 
                end using reinvestment rate, discount all negative cash flows (after initial investment) back to present 
                using financing rate, then solve for the rate that equates these two values.
              </p>
            </div>
            <div className="faq-item">
              <h5>What is a good IRR percentage?</h5>
              <p>
                A "good" IRR depends on your cost of capital, investment risk, and alternative opportunities. Generally, 
                IRR should exceed your hurdle rate (required return) to be acceptable. For corporate projects, compare 
                IRR to WACC (Weighted Average Cost of Capital): if WACC is 10%, IRR above 10% creates value. Add risk 
                premium for riskier projects: established business expansions might need 12-15% IRR, new ventures 18-25%, 
                and high-tech startups 30%+. Real estate investors typically target 15-20% IRR for value-add properties, 
                8-12% for stabilized income properties. Private equity firms seek 20-25%+ IRR.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the limitations of using IRR?</h5>
              <p>
                IRR has several important limitations you must understand. First, the reinvestment assumption: IRR assumes 
                all interim cash flows are reinvested at the IRR rate itself, which is often unrealistic. If your project 
                has 30% IRR but you can only reinvest cash at 10%, actual returns will be lower than IRR suggests. Second, 
                multiple IRRs: projects with alternating positive and negative cash flows (e.g., mining with reclamation 
                costs at end) can mathematically produce multiple valid IRR values, making interpretation impossible. 
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IRRCalculator;;